import {
  IconDefinition,
  faBook,
  faFileCode,
  faFolder,
  faKey,
  faPassport,
  faTags,
  faUserAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Fragment, ReactElement, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Highlight, TagList } from '.';
import { SearchResult } from '../types/search-result';
import classNames from '../utilities/class-names';
import { searchResultUrl } from '../utilities/search-results';
import './SearchResultsMenuItem.scss';

type SearchResultsMenuItemProps = {
  id: string;
  item: SearchResult;
  terms?: string[];
  active?: boolean;
  onSelect?: () => void;
  onHover?: () => void;
};

type SearchResultDisplay = {
  icon: IconDefinition;
  name: string;
  details: string[];
  status: string[];

  // The text shown in the row for each searchable field, used to work out
  // which matched fields can't be seen
  shown: Record<string, string | null | undefined>;
};

const MAX_VISIBLE_TAGS = 3;

const FIELD_LABELS: Record<string, string> = {
  pathName: 'path name',
  displayName: 'display name',
};

const shortId = (id: string) => id.split('-')[0];

function displaySearchResult(result: SearchResult): SearchResultDisplay {
  const status =
    result.type === 'docs'
      ? []
      : [
          ...(result.data.activated === false ? ['Deactivated'] : []),
          ...(result.data.locked ? ['Locked'] : []),
        ];

  switch (result.type) {
    case 'list':
      return {
        icon: faFolder,
        name: result.data.name || 'Unnamed list',
        details: [
          ...(result.data.pathName ? [`/${result.data.pathName}`] : []),
          shortId(result.data.id),
        ],
        status,
        shown: {
          name: result.data.name,
          pathName: result.data.pathName,
          id: shortId(result.data.id),
        },
      };
    case 'item':
      return {
        icon: faFileCode,
        name:
          result.data.description ||
          result.data.alias ||
          `Item ${shortId(result.data.id)}`,
        details: [
          `in ${result.data.listName || 'unnamed list'}`,
          ...(result.data.description && result.data.alias
            ? [result.data.alias]
            : []),
          shortId(result.data.id),
        ],
        status,
        shown: {
          description: result.data.description,
          alias: result.data.alias,
          id: shortId(result.data.id),
        },
      };
    case 'index':
      return {
        icon: faTags,
        name: result.data.name || 'Unnamed index',
        details: [
          `in ${result.data.listName || 'unnamed list'}`,
          ...(result.data.pathName ? [result.data.pathName] : []),
        ],
        status,
        shown: {
          name: result.data.name,
          pathName: result.data.pathName,
        },
      };
    case 'identity':
      return {
        icon: faPassport,
        name: result.data.name || 'Unnamed identity',
        details: [
          ...(result.data.displayName ? [result.data.displayName] : []),
          result.data.group ? `group: ${result.data.group}` : 'no group',
          shortId(result.data.id),
        ],
        status,
        shown: {
          name: result.data.name,
          displayName: result.data.displayName,
          group: result.data.group,
          id: shortId(result.data.id),
        },
      };
    case 'token':
      return {
        icon: faKey,
        name: result.data.name || 'Unnamed token',
        details: [shortId(result.data.id)],
        status,
        shown: {
          name: result.data.name,
          id: shortId(result.data.id),
        },
      };
    case 'user':
      return {
        icon: faUserAlt,
        name: result.data.displayName || 'Unnamed user',
        details: [shortId(result.data.id)],
        status,
        shown: {
          displayName: result.data.displayName,
          id: shortId(result.data.id),
        },
      };
    case 'docs':
      return {
        icon: faBook,
        name: result.data.title,
        details: [result.data.url],
        status,
        shown: {},
      };
  }
}

const containsTerm = (text: string | null | undefined, terms: string[]) =>
  !!text && terms.some(term => text.toLowerCase().includes(term));

export function SearchResultsMenuItem({
  id,
  item,
  terms = [],
  active,
  onSelect,
  onHover,
}: SearchResultsMenuItemProps): ReactElement {
  const ref = useRef<HTMLAnchorElement>(null);
  const { icon, name, details, status, shown } = displaySearchResult(item);

  // Tags that match a search term are shown first, so they aren't cut off
  const allTags = item.type === 'docs' ? [] : (item.data.tags ?? []);
  const tags = [
    ...allTags.filter(tag => containsTerm(tag, terms)),
    ...allTags.filter(tag => !containsTerm(tag, terms)),
  ].slice(0, MAX_VISIBLE_TAGS);

  // Matched fields that can't be seen in the row (e.g. a list's description)
  // are listed, so it's clear why the result was found
  const hiddenMatches = (item.type === 'docs' ? [] : (item.matches ?? []))
    .filter(field =>
      field === 'tags'
        ? !tags.some(tag => containsTerm(tag, terms))
        : !containsTerm(shown[field], terms)
    )
    .map(field => FIELD_LABELS[field] ?? field);

  useEffect(() => {
    if (active) {
      ref.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [active]);

  return (
    <Link
      ref={ref}
      id={id}
      to={searchResultUrl(item)}
      role="option"
      aria-selected={!!active}
      tabIndex={-1}
      className={classNames(
        'search-results-menu-item',
        `search-results-menu-item-${item.type}`,
        active ? 'active' : undefined
      )}
      onClick={onSelect}
      onMouseMove={onHover}
    >
      <div className="search-results-menu-item-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="search-results-menu-item-details">
        <span className="search-results-menu-item-name">
          <Highlight text={name} terms={terms} />
        </span>
        <span className="search-results-menu-item-secondary">
          {details.map((detail, i) => (
            <Fragment key={i}>
              {i > 0 && ' · '}
              <Highlight text={detail} terms={terms} />
            </Fragment>
          ))}
          {hiddenMatches.length > 0 && (
            <span className="search-results-menu-item-matched">
              {' · '}matched {hiddenMatches.join(', ')}
            </span>
          )}
        </span>
      </div>
      <div className="search-results-menu-item-status">
        {tags.length > 0 && (
          <TagList
            className="search-results-menu-item-tags"
            tags={tags}
            highlight={terms}
            linked={false}
          />
        )}
        {status.map(label => (
          <span key={label} className="search-results-menu-item-badge">
            {label}
          </span>
        ))}
      </div>
    </Link>
  );
}
